import { RecipientRole } from '@getaccept/lib-shared/src/recipients/enums/recipient-role';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';

export class SigningOrderHelper {
  static getPayloadApprover(
    recipients: Recipient[],
    recipientId: string
  ): { id: string; orderNum: number }[] {
    const approver = recipients.find(recipient => recipient.id === recipientId);
    const remainingRecipients = recipients.filter(recipient => recipient.id !== recipientId);

    return SigningOrderHelper.getPayload([
      { ...approver, role: RecipientRole.Approver, orderNum: 1 },
      ...remainingRecipients,
    ]);
  }

  static getPayload(recipients: Recipient[]): { id: string; orderNum: number }[] {
    return SigningOrderHelper.fixRecipientOrderNumber(recipients).map(({ id, orderNum }) => ({
      id,
      orderNum,
    }));
  }

  static fixRecipientOrderNumber(recipients: Recipient[]): Recipient[] {
    return recipients.reduce((acc, recipient) => {
      if (!acc.length) {
        return [{ ...recipient, orderNum: 1 }];
      }

      const previousRecipient = acc[acc.length - 1];
      const orderNum = SigningOrderHelper.calculateOrderNumer(previousRecipient, recipient);
      return [...acc, { ...recipient, orderNum }];
    }, []);
  }

  static calculateOrderNumer(previousRecipient: Recipient, recipient: Recipient): number {
    if (
      previousRecipient.role === RecipientRole.Approver &&
      recipient.role !== RecipientRole.Approver
    ) {
      return previousRecipient.orderNum + 1;
    }

    const isOrderNumGap = previousRecipient.orderNum + 1 < recipient.orderNum;
    return isOrderNumGap ? previousRecipient.orderNum + 1 : recipient.orderNum;
  }

  static getRecipientListSize(recipients: Recipient[]): number {
    if (!recipients?.length) {
      return 0;
    }
    const highestOrderNum = recipients[recipients.length - 1].orderNum;
    return highestOrderNum === recipients.length ? recipients.length : highestOrderNum + 1;
  }

  static setDragRecipientOrder(
    recipients: Recipient[],
    movedRecipient: Recipient,
    newIndex: number
  ): { id: string; orderNum: number }[] {
    const filteredRecipients: Recipient[] = recipients.filter(({ id }) => id !== movedRecipient.id);
    filteredRecipients.splice(newIndex, 0, movedRecipient);

    return filteredRecipients.map(({ id }, index: number) => ({ id, orderNum: index + 1 }));
  }

  static fixRecipientsGaps(recipients: Recipient[]): Recipient[] {
    const updatedRecipients: Recipient[] = [];
    recipients.forEach((recipient, i) => {
      const previousRecipient: Recipient = recipients[i - 1];
      const previousOrganizedRecipient: Recipient = updatedRecipients[i - 1];

      if (previousRecipient && recipient.orderNum === previousRecipient.orderNum) {
        updatedRecipients.push({ ...recipient, orderNum: previousOrganizedRecipient.orderNum });
      } else if (previousRecipient) {
        updatedRecipients.push({
          ...recipient,
          orderNum: previousOrganizedRecipient.orderNum + 1,
        });
      } else {
        updatedRecipients.push({ ...recipient, orderNum: i + 1 });
      }
    });
    return updatedRecipients;
  }
}
